import { useState, useEffect, useRef } from 'react';
import {
  Button,
  Grid,
  Typography,
  Checkbox,
  FormControlLabel,
  IconButton,
  Box,
  CircularProgress,
  TextField,
  Tab,
  Tabs,
  Paper,
  Switch
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import PickerField from '../../components/PickerField';
import FormDialog from '../../components/FormDialog';
import { Tooltip } from '@mui/material';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import { useNotifier } from '../../components/Notifier';
import { useLanguage } from '../../utils/i18n';
import { useCompanyList } from '../../stores/companyStore';
import {
  getAppSettingById,
  createAppSetting,
  updateAppSetting,
  uploadAppSettingImage,
  getAppSettingImage,
  deleteAppSettingImage
} from '../../stores/appSettingStore';
import { createAppDomain, getAppDomainList, updateAppDomain } from '../../stores/appDomainStore';
import { fleetApi } from '../../api/axiosClient';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import ElectricCarIcon from '@mui/icons-material/ElectricCar';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TimerIcon from '@mui/icons-material/Timer';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import InventoryIcon from '@mui/icons-material/Inventory';
import BadgeIcon from '@mui/icons-material/Badge';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarsIcon from '@mui/icons-material/Stars';

const WIDGETS_CONFIG = {
  revenue: [
    {
      key: 'vehicle-location',
      title: 'Vehicle Location',
      desc: 'Contains updated vehicle position data for today',
      icon: LocationOnIcon,
      color: '#3b82f6'
    },
    {
      key: 'vehicle-retase',
      title: 'Vehicle Retase',
      desc: 'Contains the accumulated total of vehicles performing ritase today',
      icon: LocalShippingIcon,
      color: '#f59e0b'
    },
    {
      key: 'vehicle-in-geolocation',
      title: 'Vehicle in Geolocation',
      desc: 'Contains accumulated data of vehicles within the geolocation today',
      icon: GpsFixedIcon,
      color: '#10b981'
    },
    {
      key: 'electric-vehicle',
      title: 'Electric Vehicle',
      desc: 'Displays the EV dashboard (currently using dummy data)',
      icon: ElectricCarIcon,
      color: '#8b5cf6'
    },
    {
      key: 'achievment',
      title: 'Achievement',
      desc: 'Displays the number of ritase performed by vehicles today, filterable by date range',
      icon: EmojiEventsIcon,
      color: '#f59e0b'
    },
    {
      key: 'vehicle-status',
      title: 'Vehicle Status',
      desc: 'Real-time updated vehicle status including last update, location, GPS, IMEI, and signal strength',
      icon: MonitorHeartIcon,
      color: '#ef4444'
    },
    {
      key: 'top-10-ritase',
      title: 'Top 10 Ritase',
      desc: 'Chart displaying the top 10 vehicles with the highest ritase today or based on a selected time period',
      icon: TrendingUpIcon,
      color: '#06b6d4'
    },
    {
      key: 'worst-10-idle-unit',
      title: 'Worst 10 Idle Unit',
      desc: 'Chart displaying the top 10 vehicles with the longest idle time today or based on a selected time period',
      icon: TimerIcon,
      color: '#f43f5e'
    }
  ],
  expense: [
    {
      key: 'fuel-consumption-per-unit',
      title: 'Fuel Consumption per unit',
      desc: 'Data table containing fuel consumption per vehicle unit',
      icon: LocalGasStationIcon,
      color: '#f59e0b'
    },
    {
      key: 'summary-fuel',
      title: 'Summary Fuel',
      desc: 'Card displaying trip summary, fuel usage, and the variance between standard and actual fuel consumption',
      icon: ReceiptIcon,
      color: '#10b981'
    },
    {
      key: 'fuel-consumption-trend',
      title: 'Fuel Consumption Trend',
      desc: 'Chart displaying fuel consumption trends per unit or accumulated based on a date range',
      icon: ShowChartIcon,
      color: '#3b82f6'
    }
  ],
  asset: [
    {
      key: 'summary-asset',
      title: 'Summary Asset',
      desc: 'Card displaying overall accumulated asset data',
      icon: InventoryIcon,
      color: '#6366f1'
    },
    {
      key: 'license-driver-reminder',
      title: 'License Driver Reminder',
      desc: 'Reminder for driver license status (active or expired)',
      icon: BadgeIcon,
      color: '#ec4899'
    },
    {
      key: 'status-operational',
      title: 'Status Operational',
      desc: 'Updated operational status of each vehicle and the number of ritase today',
      icon: CheckCircleIcon,
      color: '#14b8a6'
    },
    {
      key: 'top-driver-by-ritase',
      title: 'Top Driver By Ritase',
      desc: 'Calculated data of the highest ritase achieved by drivers',
      icon: StarsIcon,
      color: '#f59e0b'
    }
  ]
};

const defaultWidgets = {
  revenue: {
    'vehicle-location': true,
    'vehicle-retase': true,
    'vehicle-in-geolocation': true,
    'electric-vehicle': true,
    achievment: true,
    'vehicle-status': true,
    'top-10-ritase': true,
    'worst-10-idle-unit': true
  },
  expense: {
    'fuel-consumption-per-unit': true,
    'summary-fuel': true,
    'fuel-consumption-trend': true
  },
  asset: {
    'summary-asset': true,
    'license-driver-reminder': true,
    'status-operational': true,
    'top-driver-by-ritase': true
  }
};

const UploadBox = ({ label, onUpload, previewUrl, description, onRemove }) => (
  <Box
    sx={{
      border: '1px dashed #d1d5db',
      borderRadius: 2,
      p: 2,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      bgcolor: '#f9fafb',
      height: 140,
      position: 'relative',
      transition: 'all 0.2s ease-in-out',
      '&:hover': { borderColor: '#3b82f6', bgcolor: '#f0f9ff' }
    }}
  >
    {previewUrl ? (
      <Box component="img" src={previewUrl} sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 1 }} />
    ) : (
      <Typography variant="caption" color="textSecondary" sx={{ mb: 1 }}>
        No Image Selected
      </Typography>
    )}
    <Button
      variant={previewUrl ? 'contained' : 'outlined'}
      component="label"
      size="small"
      startIcon={<CloudUploadIcon />}
      sx={{
        mt: previewUrl ? 1 : 0,
        position: previewUrl ? 'absolute' : 'relative',
        bottom: previewUrl ? 8 : 'auto',
        boxShadow: previewUrl ? 2 : 0
      }}
    >
      {previewUrl ? 'CHANGE' : 'UPLOAD'}
      <input type="file" hidden accept="image/*" onChange={(e) => onUpload(e.target.files[0])} />
    </Button>
    {previewUrl && onRemove && (
      <IconButton
        size="small"
        onClick={onRemove}
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          bgcolor: 'rgba(255,255,255,0.9)',
          boxShadow: 1,
          '&:hover': { bgcolor: '#fee2e2', color: '#ef4444' }
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    )}
    {!previewUrl && description && (
      <Typography variant="caption" color="textSecondary" align="center" sx={{ mt: 1, px: 1, fontSize: '0.7rem' }}>
        {description}
      </Typography>
    )}
  </Box>
);

const convertToWebP = (file) => {
  return new Promise((resolve) => {
    if (!file || !file.type?.startsWith('image/') || file.type === 'image/webp' || file.type === 'image/svg+xml') {
      return resolve(file);
    }
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const newFile = new File([blob], file.name.replace(/\.[^/.]+$/, '') + '.webp', {
                type: 'image/webp',
                lastModified: Date.now()
              });
              resolve(newFile);
            } else {
              resolve(file);
            }
          },
          'image/webp',
          0.85
        );
      };
      img.onerror = () => resolve(file);
    };
    reader.onerror = () => resolve(file);
  });
};

const MultipleUploadBox = ({ label, description, files, onUpload, onRemove, getPreviewUrl, maxFiles }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const prevFilesLength = useRef(files.length);

  useEffect(() => {
    if (files.length > prevFilesLength.current) {
      setCurrentIndex(files.length - 1); // Navigate to newly added file
    } else if (files.length > 0 && currentIndex >= files.length) {
      setCurrentIndex(files.length - 1);
    }
    prevFilesLength.current = files.length;
  }, [files.length, currentIndex]);

  const handlePrev = () => setCurrentIndex((prev) => (prev > 0 ? prev - 1 : files.length - 1));
  const handleNext = () => setCurrentIndex((prev) => (prev < files.length - 1 ? prev + 1 : 0));

  return (
    <Box
      sx={{
        border: '1px dashed #d1d5db',
        borderRadius: 2,
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#f9fafb',
        position: 'relative',
        transition: 'all 0.2s ease-in-out',
        '&:hover': { borderColor: '#3b82f6' }
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle2" fontWeight="600">
            {label.toUpperCase()}
          </Typography>
          {description && (
            <Tooltip title={description} placement="top">
              <InfoIcon fontSize="small" color="action" />
            </Tooltip>
          )}
        </Box>
        <Button
          variant="outlined"
          component="label"
          size="small"
          startIcon={<CloudUploadIcon />}
          disabled={maxFiles && files.length >= maxFiles}
        >
          UPLOAD FILES
          <input
            type="file"
            hidden
            accept="image/*"
            multiple
            disabled={maxFiles && files.length >= maxFiles}
            onChange={(e) => {
              const selectedFiles = Array.from(e.target.files);
              onUpload(selectedFiles);
              e.target.value = null;
            }}
          />
        </Button>
      </Box>
      {files.length > 0 ? (
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: { xs: 200, sm: 300, md: 400 },
            bgcolor: '#000',
            borderRadius: 2,
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Box
            component="img"
            src={getPreviewUrl(files[currentIndex])}
            sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
          />

          <IconButton
            size="small"
            onClick={() => onRemove(currentIndex)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'rgba(255,255,255,0.9)',
              '&:hover': { bgcolor: '#fee2e2', color: '#ef4444' }
            }}
          >
            <CloseIcon />
          </IconButton>

          {files.length > 1 && (
            <>
              <IconButton
                onClick={handlePrev}
                sx={{ position: 'absolute', left: 8, bgcolor: 'rgba(255,255,255,0.5)', '&:hover': { bgcolor: 'white' } }}
              >
                <ArrowBackIosNewIcon fontSize="small" />
              </IconButton>
              <IconButton
                onClick={handleNext}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  bgcolor: 'rgba(255,255,255,0.5)',
                  '&:hover': { bgcolor: 'white' }
                }}
              >
                <ArrowForwardIosIcon fontSize="small" />
              </IconButton>
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 8,
                  bgcolor: 'rgba(0,0,0,0.5)',
                  color: 'white',
                  px: 2,
                  py: 0.5,
                  borderRadius: 10,
                  fontSize: '0.8rem'
                }}
              >
                {currentIndex + 1} / {files.length}
              </Box>
            </>
          )}
        </Box>
      ) : (
        <Box sx={{ height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="caption" color="textSecondary">
            No Images Selected
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default function AppSettingAddEdit({ open, id, onClose, onSuccess }) {
  const isEdit = Boolean(id);
  const notify = useNotifier();
  const { t } = useLanguage();
  const { companyOptions } = useCompanyList();

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Form States
  const [activeWidgetTab, setActiveWidgetTab] = useState('revenue');
  const [widgets, setWidgets] = useState(defaultWidgets);
  const [companyId, setCompanyId] = useState(null);
  const [webTitle, setWebTitle] = useState('');
  const [loginTitle, setLoginTitle] = useState('');
  const [headerColor, setHeaderColor] = useState('#2196f3');

  const [useDefaultHeaderLogo, setUseDefaultHeaderLogo] = useState(false);
  const [useDefaultLoginLogo, setUseDefaultLoginLogo] = useState(false);
  const [useDefaultFavicon, setUseDefaultFavicon] = useState(false);
  const [headerLogo, setHeaderLogo] = useState(null);
  const [loginLogo, setLoginLogo] = useState(null);
  const [favicon, setFavicon] = useState(null);
  const [banners, setBanners] = useState([]);
  const [deletedImages, setDeletedImages] = useState([]);

  const [customerPointLabel, setCustomerPointLabel] = useState('');
  const [poolLabel, setPoolLabel] = useState('');
  const [geofenceLabel, setGeofenceLabel] = useState('');

  // Domain States
  const [showDomainInput, setShowDomainInput] = useState(false);
  const [domainId, setDomainId] = useState(null);
  const [domainName, setDomainName] = useState('');
  const [useSecondLevel, setUseSecondLevel] = useState(false);

  // Errors
  const [errors, setErrors] = useState({});

  const [cacheBuster, setCacheBuster] = useState(Date.now());

  useEffect(() => {
    if (open) {
      setCacheBuster(Date.now());
    }
    if (isEdit && open) {
      fetchData();
    } else if (open) {
      resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, open]);

  const getPreviewUrl = (fileOrUrl) => {
    if (!fileOrUrl) return null;
    if (fileOrUrl instanceof File) return URL.createObjectURL(fileOrUrl);
    if (typeof fileOrUrl === 'string' && (fileOrUrl.startsWith('http') || fileOrUrl.startsWith('data:'))) {
      if (fileOrUrl.startsWith('data:')) return fileOrUrl;
      const separator = fileOrUrl.includes('?') ? '&' : '?';
      return `${fileOrUrl}${separator}t=${cacheBuster}`;
    }
    return fileOrUrl;
  };

  const handleRemoveBanner = (idx) => {
    const removedBanner = banners[idx];
    if (typeof removedBanner === 'string' && id) {
      // Defer API deletion to handleSave
      setDeletedImages((prev) => [...prev, { type: 'banner', url: removedBanner }]);
    }
    setBanners((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleRemoveLogo = (type, currentVal, setter) => {
    if (typeof currentVal === 'string' && id) {
      setDeletedImages((prev) => [...prev, { type, url: currentVal }]);
    }
    setter(null);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const resp = await getAppSettingById(id);
      let data = resp;
      if (resp && Array.isArray(resp.data)) {
        data = resp.data[0];
      } else if (resp && resp.data) {
        data = resp.data;
      } else if (Array.isArray(resp)) {
        data = resp[0];
      }

      if (data) {
        const compId = data.company || data.company_id;
        let compObj = companyOptions.find((c) => String(c.value) === String(compId));
        if (!compObj && compId) {
          compObj = { value: compId, label: data.join?.company_name || compId };
        }
        setCompanyId(compObj || null);

        setWebTitle(data.web_attribution?.title || data.web_title || '');
        setLoginTitle(data.login?.title || data.login_title || '');
        setHeaderColor(data.header?.color || data.header_color || '#2196f3');
        setUseDefaultHeaderLogo(Boolean(data.is_default_header_logo || data.use_default_header_logo));
        setUseDefaultLoginLogo(Boolean(data.is_default_login_logo));
        setUseDefaultFavicon(Boolean(data.is_default_favicon));
        setCustomerPointLabel(data.labels?.customer_point || data.labels_customer_point || data.customer_point_label || '');
        setPoolLabel(data.labels?.pool || data.labels_pool || data.pool_label || '');
        setGeofenceLabel(data.labels?.geofence || data.labels_geofence || data.geofence_label || '');

        if (data.widgets) {
          const parsedWidgets = typeof data.widgets === 'string' ? JSON.parse(data.widgets) : data.widgets;
          setWidgets({
            revenue: { ...defaultWidgets.revenue, ...(parsedWidgets?.revenue || {}) },
            expense: { ...defaultWidgets.expense, ...(parsedWidgets?.expense || {}) },
            asset: { ...defaultWidgets.asset, ...(parsedWidgets?.asset || {}) }
          });
        } else {
          setWidgets(defaultWidgets);
        }

        try {
          const imgResp = await getAppSettingImage(id);
          if (imgResp) {
            if (imgResp.header_logo?.[0]) setHeaderLogo(imgResp.header_logo[0]);
            if (imgResp.login_logo?.[0]) setLoginLogo(imgResp.login_logo[0]);
            if (imgResp.favicon?.[0]) setFavicon(imgResp.favicon[0]);
            if (imgResp.banner?.length > 0) setBanners(imgResp.banner);
          }
        } catch (imgErr) {
          console.error('Failed to load images', imgErr);
        }

        try {
          const domainResp = await getAppDomainList({
            start: 0,
            length: -1,
            search: '',
            searchField: 'all',
            dateField: '',
            startDate: '',
            endDate: ''
          });
          const domainList = domainResp?.data || domainResp || [];
          const matchedDomain = domainList.find((d) => String(d.app_setting) === String(id));
          if (matchedDomain) {
            setDomainId(matchedDomain.id);
            setShowDomainInput(true);
            const fullDomain = matchedDomain.web_domains || '';
            if (fullDomain.endsWith('.oslog.id')) {
              setDomainName(fullDomain.replace('.oslog.id', ''));
              setUseSecondLevel(true);
            } else {
              setDomainName(fullDomain);
              setUseSecondLevel(false);
            }
          }
        } catch (domainErr) {
          console.error('Failed to load app domain', domainErr);
        }
      }
    } catch {
      notify({ message: 'Failed to load App Setting data', severity: 'error' });
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    const storedCompanyId = localStorage.getItem('companyId');
    const defaultCompany = storedCompanyId ? companyOptions.find((c) => String(c.value) === String(storedCompanyId)) || null : null;
    setCompanyId(defaultCompany);
    setWebTitle('');
    setLoginTitle('');
    setHeaderColor('#2196f3');
    setUseDefaultHeaderLogo(false);
    setUseDefaultLoginLogo(false);
    setUseDefaultFavicon(false);
    setHeaderLogo(null);
    setLoginLogo(null);
    setFavicon(null);
    setBanners([]);
    setDeletedImages([]);
    setCustomerPointLabel('');
    setPoolLabel('');
    setGeofenceLabel('');
    setShowDomainInput(false);
    setDomainId(null);
    setDomainName('');
    setUseSecondLevel(false);
    setWidgets(defaultWidgets);
    setActiveWidgetTab('revenue');
    setErrors({});
  };

  const validate = () => {
    let temp = {};
    if (!companyId) temp.companyId = 'This field is required';
    if (!webTitle.trim()) temp.webTitle = 'This field is required';
    setErrors(temp);
    return Object.keys(temp).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        company: companyId.value,
        web_title: webTitle,
        login_title: loginTitle,
        header_color: headerColor,
        labels_customer_point: customerPointLabel,
        labels_pool: poolLabel,
        labels_geofence: geofenceLabel,
        is_default_login_logo: useDefaultLoginLogo,
        is_default_header_logo: useDefaultHeaderLogo,
        is_default_favicon: useDefaultFavicon,
        widgets: widgets
      };

      let savedSettingId = id;
      if (isEdit) {
        await updateAppSetting(id, payload);
        notify({ message: t('appSetting.updatedAppSetting', 'App Setting updated successfully'), severity: 'success' });
      } else {
        const res = await createAppSetting(payload);
        savedSettingId = res?.id;
        notify({ message: t('appSetting.createAppSetting', 'App Setting created successfully'), severity: 'success' });
      }

      // Handle Image Uploads if any selected
      if (savedSettingId) {
        if (headerLogo && headerLogo instanceof File) await uploadAppSettingImage(savedSettingId, headerLogo, 'header_logo');
        if (loginLogo && loginLogo instanceof File) await uploadAppSettingImage(savedSettingId, loginLogo, 'login_logo');
        if (favicon && favicon instanceof File) await uploadAppSettingImage(savedSettingId, favicon, 'favicon');
        // Banners logic: upload all newly added banner files
        if (banners.length > 0) {
          for (const banner of banners) {
            if (banner instanceof File) {
              await uploadAppSettingImage(savedSettingId, banner, 'banner');
            }
          }
        }

        // Execute deferred image deletions (banners & logos)
        if (deletedImages.length > 0) {
          for (const item of deletedImages) {
            try {
              const urlParts = item.url.split('/');
              const fileNameWithParams = urlParts[urlParts.length - 1];
              const fileName = fileNameWithParams.split('?')[0];
              await deleteAppSettingImage(savedSettingId, item.type, fileName);
            } catch (err) {
              console.error(`Failed to delete ${item.type} during save:`, err);
            }
          }
          setDeletedImages([]);
        }
      }

      // Save Domain if added
      if (showDomainInput && domainName) {
        const finalDomain = useSecondLevel ? `${domainName}.oslog.id` : domainName;
        if (domainId) {
          await updateAppDomain(domainId, { web_domains: finalDomain, app_setting: savedSettingId, company: companyId.value });
          notify({ message: 'Domain updated successfully', severity: 'success' });
        } else {
          await createAppDomain({ web_domains: finalDomain, app_setting: savedSettingId, company: companyId.value });
          notify({ message: 'Domain added successfully', severity: 'success' });
        }
      }
      // Refresh localStorage so UI updates instantly
      try {
        const currentCompanyId = localStorage.getItem('companyId');
        // Only update local storage if editing the currently active company's settings,
        // or if there's no active company check required, update anyway to reflect changes.
        if (!currentCompanyId || String(currentCompanyId) === String(companyId.value)) {
          // Use the exact same search endpoint as AuthLogin.jsx to ensure the format matches (nested objects instead of flat)
          const response = await fleetApi.post('/app-setting/search', { company: companyId.value });
          if (response && response.data) {
            let appSettingData = response.data.data ?? response.data;
            if (Array.isArray(appSettingData)) {
              if (appSettingData.length > 0) {
                appSettingData = appSettingData[0];
              }
            }
            if (appSettingData) {
              localStorage.setItem('appSetting', JSON.stringify(appSettingData));
              console.info('App setting localStorage refreshed with search format.');
            }
          }
        }
      } catch (refreshErr) {
        console.error('Failed to refresh appSetting in localStorage', refreshErr);
      }

      onSuccess();
      onClose();
    } catch (err) {
      console.error(err);
      notify({ message: t('appSetting.errorSavingAppSetting', 'Error saving App Setting'), severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const dialogActions = (
    <>
      <Button onClick={onClose} disabled={submitting} sx={{ color: 'text.secondary', textTransform: 'none' }}>
        {t('buttonTitle.cancel', 'Cancel')}
      </Button>
      <Button
        onClick={handleSave}
        variant="contained"
        disabled={submitting || loading}
        startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
        sx={{ textTransform: 'none', borderRadius: 1, px: 3, fontWeight: 700 }}
      >
        {submitting
          ? t('buttonTitle.processing', 'Processing...')
          : isEdit
            ? t('buttonTitle.saveChanges', 'Save Changes')
            : t('buttonTitle.create', 'Create')}
      </Button>
    </>
  );

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={isEdit ? t('appSetting.editAppSetting', 'Edit App Setting') : t('appSetting.addAppSetting', 'Add App Setting')}
      fullScreenMode={true}
      actions={dialogActions}
    >
      <Box sx={{ width: '100%', pt: 1 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {/* Company Info */}
            <Grid item size={{ xs: 12 }}>
              <PickerField
                label={t('general.company', 'Company *')}
                value={companyId ? companyId.value : null}
                options={companyOptions}
                onChange={(opt) => {
                  const selected = (companyOptions || []).find((o) => String(o.value) === String(opt?.value ?? opt?.id ?? opt)) ?? null;
                  setCompanyId(selected);
                }}
                getOptionId={(o) => o?.value ?? o?.id ?? o}
                getOptionLabel={(o) => o?.label ?? o?.name ?? String(o?.value ?? o?.id ?? o)}
                dialogTitle={t('general.selectCompany', 'Select Company')}
                error={!!errors.companyId}
                helperText={errors.companyId}
              />
            </Grid>

            <Grid item size={{ xs: 12, sm: 6 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                <Typography variant="subtitle2">{t('appSetting.webTitle', 'Web Title')}</Typography>
                <Tooltip
                  title={t('appSetting.theMainTitle', 'The main title of the web application shown in the browser tab and web header.')}
                  placement="top"
                >
                  <InfoIcon fontSize="small" color="action" />
                </Tooltip>
              </Box>
              <TextField
                fullWidth
                placeholder="e.g. Fleet Management System"
                value={webTitle}
                onChange={(e) => setWebTitle(e.target.value)}
                error={!!errors.webTitle}
                helperText={errors.webTitle}
              />
            </Grid>

            <Grid item size={{ xs: 12, sm: 6 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                <Typography variant="subtitle2">{t('appSetting.loginTitle', 'Login Title')}</Typography>
                <Tooltip title={t('appSetting.loginTitleTooltip', 'The title displayed on the login page screen.')} placement="top">
                  <InfoIcon fontSize="small" color="action" />
                </Tooltip>
              </Box>
              <TextField fullWidth placeholder="e.g. Welcome to FMS" value={loginTitle} onChange={(e) => setLoginTitle(e.target.value)} />
            </Grid>

            <Grid item size={{ xs: 12 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, display: 'block' }}>
                {t('appSetting.headerColor', 'Header Color')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <input
                  type="color"
                  value={headerColor}
                  onChange={(e) => setHeaderColor(e.target.value)}
                  style={{ width: '40px', height: '40px', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer', padding: 0 }}
                />
                <TextField
                  value={headerColor}
                  onChange={(e) => setHeaderColor(e.target.value)}
                  size="small"
                  sx={{ width: 150 }}
                  inputProps={{ maxLength: 7 }}
                />
              </Box>
            </Grid>

            {/* Upload Files Section */}
            <Grid item size={{ xs: 12 }}>
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                Upload Files
              </Typography>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <FormControlLabel
                  control={<Checkbox checked={useDefaultHeaderLogo} onChange={(e) => setUseDefaultHeaderLogo(e.target.checked)} />}
                  label={t('appSetting.useDefaultHeaderLogo', 'Use Default Header Logo')}
                />
                <FormControlLabel
                  control={<Checkbox checked={useDefaultLoginLogo} onChange={(e) => setUseDefaultLoginLogo(e.target.checked)} />}
                  label={t('appSetting.useDefaultLoginLogo', 'Use Default Login Logo')}
                />
                <FormControlLabel
                  control={<Checkbox checked={useDefaultFavicon} onChange={(e) => setUseDefaultFavicon(e.target.checked)} />}
                  label={t('appSetting.useDefaultFavicon', 'Use Default Favicon')}
                />
              </Box>
            </Grid>

            <Grid item size={{ xs: 12, sm: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle2" sx={{ display: 'block' }}>
                  Upload Web Logo
                </Typography>
                <Tooltip
                  title={t('appSetting.webLogoTooltip', 'Logo displayed on the top left of the dashboard header navigation.')}
                  placement="top"
                >
                  <InfoIcon fontSize="small" color="action" />
                </Tooltip>
              </Box>
              <UploadBox
                label={t('appSetting.webLogo', 'Web Logo')}
                description="Dimensions: ~200x50px (Ratio 4:1). Transparent PNG recommended."
                onUpload={(file) => setHeaderLogo(file)}
                previewUrl={getPreviewUrl(headerLogo)}
                onRemove={() => handleRemoveLogo('header_logo', headerLogo, setHeaderLogo)}
              />
            </Grid>
            <Grid item size={{ xs: 12, sm: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle2" sx={{ display: 'block' }}>
                  Upload {t('appSetting.loginLogo', 'Login Logo')}
                </Typography>
                <Tooltip title={t('appSetting.loginTitleTooltip', 'The title displayed on the login page screen.')} placement="top">
                  <InfoIcon fontSize="small" color="action" />
                </Tooltip>
              </Box>
              <UploadBox
                label={t('appSetting.loginLogo', 'Login Logo')}
                description="Dimensions: ~300x100px (Ratio 3:1). Transparent PNG recommended."
                onUpload={(file) => setLoginLogo(file)}
                previewUrl={getPreviewUrl(loginLogo)}
                onRemove={() => handleRemoveLogo('login_logo', loginLogo, setLoginLogo)}
              />
            </Grid>
            <Grid item size={{ xs: 12, sm: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle2" sx={{ display: 'block' }}>
                  Upload {t('appSetting.favicon', 'Favicon')}
                </Typography>
                <Tooltip title={t('appSetting.faviconTooltip', 'Small icon shown on the browser tab.')} placement="top">
                  <InfoIcon fontSize="small" color="action" />
                </Tooltip>
              </Box>
              <UploadBox
                label={t('appSetting.favicon', 'Favicon')}
                description="Dimensions: 32x32px or 64x64px (Ratio 1:1, ICO or PNG)."
                onUpload={(file) => setFavicon(file)}
                previewUrl={getPreviewUrl(favicon)}
                onRemove={() => handleRemoveLogo('favicon', favicon, setFavicon)}
              />
            </Grid>

            <Grid item size={{ xs: 12 }}>
              <MultipleUploadBox
                label={t('appSetting.loginBanners', 'Login Banners (Slideshow, Maximum of 4 images for the banner')}
                description={t(
                  'appSetting.loginBannersDescription',
                  'Upload multiple images to create a background slideshow on the login page. Recommended size: 1920x1080px (Ratio 16:9).'
                )}
                files={banners}
                maxFiles={4}
                getPreviewUrl={getPreviewUrl}
                onUpload={async (newFiles) => {
                  if (banners.length + newFiles.length > 4) {
                    notify({ message: t('appSetting.maximumOf4Images', 'Maximum of 4 images for the banner.'), severity: 'warning' });
                  }
                  const allowedNewFiles = newFiles.slice(0, Math.max(0, 4 - banners.length));
                  const webpFiles = await Promise.all(allowedNewFiles.map(convertToWebP));
                  setBanners([...banners, ...webpFiles]);
                }}
                onRemove={handleRemoveBanner}
              />
            </Grid>

            {/* Labels Section */}
            <Grid item size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, mb: 1 }}>
                <Typography variant="h6">{t('appSetting.customLabels', 'Custom Labels')}</Typography>
                <Tooltip
                  title={t(
                    'appSetting.customLabelsTooltip',
                    'Override the default terminology used across the application with your preferred terms.'
                  )}
                  placement="top"
                >
                  <InfoIcon color="action" />
                </Tooltip>
              </Box>
            </Grid>

            <Grid item size={{ xs: 12, sm: 4 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, display: 'block' }}>
                {t('appSetting.jettyLabel', 'Jetty Label')}
              </Typography>
              <TextField
                placeholder="e.g. Jetty"
                fullWidth
                value={customerPointLabel}
                onChange={(e) => setCustomerPointLabel(e.target.value)}
              />
            </Grid>
            <Grid item size={{ xs: 12, sm: 4 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, display: 'block' }}>
                {t('appSetting.pitLabel', 'PIT Label')}
              </Typography>
              <TextField placeholder="e.g. Garage" fullWidth value={poolLabel} onChange={(e) => setPoolLabel(e.target.value)} />
            </Grid>
            <Grid item size={{ xs: 12, sm: 4 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, display: 'block' }}>
                {t('appSetting.geofenceLabel', 'Geofence Label')}
              </Typography>
              <TextField
                placeholder="e.g. Boundary Zone"
                fullWidth
                value={geofenceLabel}
                onChange={(e) => setGeofenceLabel(e.target.value)}
              />
            </Grid>

            {/* Widgets Configuration Section */}
            <Grid item size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 3, mb: 1 }}>
                <Typography variant="h6">Dashboard Widgets</Typography>
                <Tooltip title="Configure which widgets are visible on the dashboard for this company." placement="top">
                  <InfoIcon color="action" />
                </Tooltip>
              </Box>

              <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden', mt: 1 }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: '#f8fafc' }}>
                  <Tabs value={activeWidgetTab} onChange={(e, val) => setActiveWidgetTab(val)} textColor="primary" indicatorColor="primary">
                    <Tab label="Revenue" value="revenue" sx={{ fontWeight: 600, textTransform: 'none' }} />
                    <Tab label="Expense" value="expense" sx={{ fontWeight: 600, textTransform: 'none' }} />
                    <Tab label="Asset" value="asset" sx={{ fontWeight: 600, textTransform: 'none' }} />
                  </Tabs>
                </Box>
                <Box sx={{ p: 3, bgcolor: '#f1f5f9' }}>
                  <Grid container spacing={2}>
                    {WIDGETS_CONFIG[activeWidgetTab].map((widget) => {
                      const Icon = widget.icon;
                      const isChecked = widgets[activeWidgetTab][widget.key];

                      return (
                        <Grid item size={{ xs: 12, md: 6 }} key={widget.key}>
                          <Paper
                            elevation={0}
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              display: 'flex',
                              gap: 2,
                              alignItems: 'center',
                              bgcolor: '#fff',
                              border: '1px solid',
                              borderColor: isChecked ? '#94a3b8' : '#e2e8f0',
                              transition: 'all 0.2s',
                              '&:hover': {
                                borderColor: isChecked ? '#64748b' : '#cbd5e1',
                                boxShadow: '0 2px 10px rgba(0,0,0,0.02)'
                              }
                            }}
                          >
                            <Box
                              className="widget-icon"
                              sx={{
                                width: 44,
                                height: 44,
                                borderRadius: '10px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: isChecked ? `${widget.color}1A` : '#f1f5f9',
                                color: isChecked ? widget.color : '#94a3b8',
                                flexShrink: 0,
                                transition: 'all 0.2s'
                              }}
                            >
                              <Icon fontSize="small" />
                            </Box>

                            <Box sx={{ flex: 1 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e293b', mb: 0.25, lineHeight: 1.2 }}>
                                {widget.title}
                              </Typography>
                              <Typography variant="body2" sx={{ color: '#64748b', lineHeight: 1.3, fontSize: '0.8rem' }}>
                                {widget.desc}
                              </Typography>
                            </Box>

                            <Switch
                              checked={isChecked}
                              onChange={(e) => {
                                setWidgets((prev) => ({
                                  ...prev,
                                  [activeWidgetTab]: {
                                    ...prev[activeWidgetTab],
                                    [widget.key]: e.target.checked
                                  }
                                }));
                              }}
                              color="primary"
                            />
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              </Paper>
            </Grid>

            {/* App Domains Section */}
            <Grid item size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, mb: 1 }}>
                <Typography variant="h6">{t('appSetting.appDomains', 'App Domains')}</Typography>
                <Tooltip
                  title="Link a specific domain (like fleet.yourcompany.com) so these settings apply automatically when users access that URL."
                  placement="top"
                >
                  <InfoIcon color="action" />
                </Tooltip>
              </Box>

              {!showDomainInput ? (
                <Button variant="outlined" color="primary" onClick={() => setShowDomainInput(true)}>
                  {t('appSetting.appDomain', 'APP DOMAIN')}
                </Button>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                  <Typography variant="subtitle2">{t('appSetting.domain', 'Domain')}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TextField
                      fullWidth
                      placeholder="e.g. fleet"
                      value={domainName}
                      onChange={(e) => setDomainName(e.target.value)}
                      InputProps={useSecondLevel ? { endAdornment: <Typography color="textSecondary">.oslog.id</Typography> } : {}}
                    />
                    <FormControlLabel
                      control={<Checkbox checked={useSecondLevel} onChange={(e) => setUseSecondLevel(e.target.checked)} />}
                      label={t('appSetting.useSecondLevelDomain', 'Use Second-Level Domain (.oslog.id)')}
                      sx={{ whiteSpace: 'nowrap' }}
                    />
                    <IconButton onClick={() => setShowDomainInput(false)} color="error">
                      <CloseIcon />
                    </IconButton>
                  </Box>
                </Box>
              )}
            </Grid>
          </Grid>
        )}
      </Box>
    </FormDialog>
  );
}
